import { Injectable } from '@angular/core';
import { SettingsHelper } from '@core/services/helpers/settings.service';
import { GeocoderService } from '@core/services/http-services/geocoder.service';
import { Geolocation } from '@ionic-native/geolocation/ngx';
import { Platform } from '@ionic/angular';
import { from, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { LatLng } from '../../models/google-location';

export type GeoPositionPermissionStatus = 'PERMISSION_GRANTED' | 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'ERROR';
@Injectable({
  providedIn: 'root'
})
export class GeocoderHelperService {

  constructor(
    private geoService: GeocoderService,
    private geolocation: Geolocation,
    private platform: Platform,
    private settingsHelper: SettingsHelper
  ) { }

  getCurrentPositionCoordinates() {
    return this.getPosition().then(({ coords }) => coords );
  }

  getCurrentPosition() {
    return from(this.getPosition());
  }

  geocodeAddressFirstResult(address: string) {
    return this.geocodeAddress(address)
      .pipe(
        map(results => results[0])
      );
  }

  geocodeLatLngFirstResult(address: LatLng) {
    return this.geocodeLatLng(address)
      .pipe(
        map(results => results[0])
      );
  }

  geocodeLatLng(address: LatLng) {
    return this.geoService.geocodeLatLng(address.lat, address.lng).pipe(
      map(res => res || {}),
      map(({ status, results }) => status === 'OK' ? results : throwError({}))
    );
  }

  checkLocationUserPermission() {
    return this.getCurrentPosition().toPromise()
      .then(() => 'PERMISSION_GRANTED')
      .catch(error => {
        switch (error?.code) {
          case 1: return 'PERMISSION_DENIED';
          case 2: return 'POSITION_UNAVAILABLE';
          case 3: return 'TIMEOUT';
          default: return 'ERROR';
        }
      }) as Promise<GeoPositionPermissionStatus>;
  }

  onUserDeniedLocationPermission(onReject?: () => void) {
    this.settingsHelper.openAppSettingsModalWithCustomMessage(
      'PERMISSIONS.LOCATION_MESSAGE',
      '',
      onReject
    );
  }

  private geocodeAddress(address: string) {
    return this.geoService.geocodeAddress(address)
      .pipe(
        map(res => res || {}),
        map(({ status, results }) => status === 'OK' ? results : throwError({}))
      );
  }

  private getPosition() {
    const getWebCurrentPosition = this.geoService.getCurrentPosition().toPromise();
    const getAppCurrentPosition = this.geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });

    return (this.platform.is('cordova')
      ? getAppCurrentPosition
      : getWebCurrentPosition
    );
  }
}
